import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ImageSquare, Trash, UploadSimple } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const THEMES = [
  { value: 'classic', label: 'Classique (Bleu)', color: '#002FA7' },
  { value: 'modern', label: 'Moderne (Rouge)', color: '#E94560' },
  { value: 'nature', label: 'Nature (Vert)', color: '#2D6A4F' },
  { value: 'elegant', label: 'Elegant (Or)', color: '#D4AF37' },
];

export const InvoiceSettings = ({ invoiceSettings, setInvoiceSettings, onSave }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Logo trop lourd (max 4 Mo)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/invoicing/settings/logo`, fd, { withCredentials: true });
      setInvoiceSettings({ ...invoiceSettings, logo_url: res.data.logo_url });
      toast.success('Logo televerse - n\'oubliez pas de sauvegarder');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload logo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Supprimer le logo ?')) return;
    try {
      await axios.delete(`${API_URL}/api/invoicing/settings/logo`, { withCredentials: true });
      setInvoiceSettings({ ...invoiceSettings, logo_url: '' });
      toast.success('Logo supprime');
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  return (
    <div className="card" style={{ maxWidth: '900px' }}>
      <div className="card-header"><h3 className="card-title">Parametres de facturation</h3></div>
      <form onSubmit={onSave} style={{ padding: '24px' }}>
        {/* LOGO */}
        <div className="form-group" style={{ marginBottom: 20, padding: 16, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8 }}>
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ImageSquare size={16} weight="bold" /> Logo de l'entreprise (remplace le nom dans le PDF)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
            <div style={{ width: 140, height: 80, border: '1px dashed #9CA3AF', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', overflow: 'hidden' }}>
              {invoiceSettings.logo_url ? (
                <img
                  src={invoiceSettings.logo_url.startsWith('http') ? invoiceSettings.logo_url : `${API_URL}${invoiceSettings.logo_url}`}
                  alt="Logo"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  data-testid="invoice-logo-preview"
                />
              ) : (
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>Aucun logo</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoSelect} style={{ display: 'none' }} data-testid="invoice-logo-input" />
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="invoice-logo-upload-btn">
                <UploadSimple size={14} weight="bold" /> {uploading ? 'Telechargement...' : (invoiceSettings.logo_url ? 'Remplacer le logo' : 'Televerser un logo')}
              </button>
              {invoiceSettings.logo_url && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleDeleteLogo} style={{ color: '#DC2626' }} data-testid="invoice-logo-delete-btn">
                  <Trash size={14} /> Supprimer le logo
                </button>
              )}
              <span style={{ fontSize: 11, color: '#6B7280' }}>PNG / JPG / WEBP - max 4 Mo</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div className="form-group">
            <label className="form-label">Nom de la societe</label>
            <input type="text" className="form-input" value={invoiceSettings.company_name || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_name: e.target.value })} data-testid="settings-company-name" />
          </div>
          <div className="form-group">
            <label className="form-label">Slogan / Tagline (optionnel)</label>
            <input type="text" className="form-input" value={invoiceSettings.company_tagline || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_tagline: e.target.value })} placeholder="Ex: Constructions de qualite depuis 2010" data-testid="settings-tagline" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input type="email" className="form-input" value={invoiceSettings.company_email || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_email: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Telephone</label>
            <input type="text" className="form-input" value={invoiceSettings.company_phone || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_phone: e.target.value })} />
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label className="form-label">Adresse</label>
            <input type="text" className="form-input" value={invoiceSettings.company_address || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_address: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">SIRET</label>
            <input type="text" className="form-input" value={invoiceSettings.company_siret || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_siret: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">N TVA intracommunautaire</label>
            <input type="text" className="form-input" value={invoiceSettings.company_tva_number || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_tva_number: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Capital social</label>
            <input type="text" className="form-input" value={invoiceSettings.company_capital || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_capital: e.target.value })} placeholder="Ex: SARL au capital de 1000 EUR" data-testid="settings-capital" />
          </div>
          <div className="form-group">
            <label className="form-label">Code APE / NAF</label>
            <input type="text" className="form-input" value={invoiceSettings.company_ape_code || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_ape_code: e.target.value })} placeholder="Ex: 4329A" data-testid="settings-ape" />
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label className="form-label">Assurance professionnelle</label>
            <input type="text" className="form-input" value={invoiceSettings.company_insurance || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, company_insurance: e.target.value })} placeholder="Ex: SMA COURTAGE - 1254002/002132261" data-testid="settings-insurance" />
          </div>
          <div className="form-group">
            <label className="form-label">IBAN</label>
            <input type="text" className="form-input" value={invoiceSettings.bank_iban || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, bank_iban: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">BIC</label>
            <input type="text" className="form-input" value={invoiceSettings.bank_bic || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, bank_bic: e.target.value })} />
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '16px' }}>
          <label className="form-label">Conditions de paiement</label>
          <input type="text" className="form-input" value={invoiceSettings.payment_terms || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, payment_terms: e.target.value })} />
        </div>

        <div className="form-group">
          <label className="form-label">Taux horaire de main d'oeuvre (EUR/h) - utilise par l'editeur d'article</label>
          <input
            type="number"
            step="0.5"
            min="0"
            data-testid="settings-labor-hourly-rate"
            className="form-input"
            value={invoiceSettings.labor_hourly_rate ?? 35}
            onChange={(e) => setInvoiceSettings({ ...invoiceSettings, labor_hourly_rate: parseFloat(e.target.value) || 0 })}
            style={{ maxWidth: 200 }}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Mentions legales</label>
          <textarea className="form-input" rows={3} value={invoiceSettings.legal_mentions || ''} onChange={(e) => setInvoiceSettings({ ...invoiceSettings, legal_mentions: e.target.value })} data-testid="settings-legal-mentions" />
        </div>

        <div className="form-group">
          <label className="form-label">Theme de la facture</label>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {THEMES.map(t => (
              <div key={t.value} onClick={() => setInvoiceSettings({ ...invoiceSettings, theme: t.value })}
                data-testid={`theme-${t.value}`}
                style={{
                  padding: '12px 20px', borderRadius: '6px', cursor: 'pointer',
                  border: invoiceSettings.theme === t.value ? `2px solid ${t.color}` : '2px solid #E2E2E6',
                  backgroundColor: invoiceSettings.theme === t.value ? `${t.color}10` : 'white',
                  display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: t.color }} />
                <span style={{ fontSize: '0.85rem', fontWeight: invoiceSettings.theme === t.value ? '700' : '400' }}>{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        <button type="submit" className="btn btn-primary" style={{ marginTop: '16px' }} data-testid="save-settings-btn">Sauvegarder les parametres</button>
      </form>
    </div>
  );
};
